import React from "react";
import { SxStyleProp } from "theme-ui";
import { AddCircleIcon } from "../icons";
import {
  Popover as PopoverBase,
  PopoverTrigger,
  PopoverContent,
} from "../Popover/Popover";
import Box, { Props as BoxProps } from "../Box/Box";
import Flex from "../Flex/Flex";
import Button, { Props as ButtonProps } from "../Button/Button";
import InputBase from "../Input/Input";

const SelectTableContext = React.createContext<SelectTableContext>(
  {} as SelectTableContext,
);
const useSelectTableContext = () => React.useContext(SelectTableContext);

interface SelectTableContext {
  isOpen?: boolean;
  onOpen: (event: any) => void;
  onClose: (event: any) => void;
}

const Popover: React.FunctionComponent = ({ children, ...restProps }) => {
  const { isOpen, onClose, onOpen } = useSelectTableContext();
  return (
    <PopoverBase
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      {...restProps}
    >
      {children}
    </PopoverBase>
  );
};

interface InputProps extends React.HTMLAttributes<HTMLInputElement> {
  value: string;
  buttonProps?: ButtonProps & BoxProps;
  withButton?: boolean;
  placeholderJSX?: JSX.Element;
}

const Input: React.FunctionComponent<InputProps> = ({
  withButton,
  buttonProps,
  ...restProps
}) => {
  const { isOpen } = useSelectTableContext();
  return (
    <PopoverTrigger>
      <Flex align="center" sx={{ flex: 1, position: "relative" }}>
        <InputBase autoComplete="new-password" {...restProps} />
        {withButton && (
          <Button
            type="button"
            sx={{
              position: "absolute",
              right: "2px",
              height: "90%",
              px: 2,
              py: 1,
              bg: "white",
              border: "0",
            }}
            {...buttonProps}
          >
            <AddCircleIcon
              color="primary"
              size="20px"
              sx={{
                transition: "transform 0.2s",
                transform: isOpen ? "rotate(45deg)" : "none",
              }}
            />
          </Button>
        )}
      </Flex>
    </PopoverTrigger>
  );
};

const InputResults: React.FunctionComponent<{ sx?: SxStyleProp }> = ({
  sx,
  ...restProps
}) => {
  return (
    <PopoverContent>
      <Box
        sx={{
          my: 2,
          width: "100%",
          border: "1px solid",
          borderColor: "border",
          borderRadius: "default",
          bg: "white",
          ...(sx as any),
        }}
        {...restProps}
      />
    </PopoverContent>
  );
};

const Content: React.FunctionComponent<{ sx?: SxStyleProp }> = ({
  sx,
  ...restProps
}) => {
  return (
    <Box
      sx={{
        my: 2,
        width: "100%",
        border: "1px solid",
        borderColor: "border",
        borderRadius: "default",
        bg: "white",
        overflow: "auto",
        ...(sx as any),
      }}
      {...restProps}
    />
  );
};

interface Props extends SelectTableContext {
  sx?: SxStyleProp;
}

interface SelectTableComposition {
  Popover: React.FunctionComponent;
  Input: React.FunctionComponent<InputProps>;
  InputResults: React.FunctionComponent;
  Content: React.FunctionComponent;
}

const SelectTable: React.FunctionComponent<Props> &
  SelectTableComposition = props => {
  const { children, isOpen, onOpen, onClose } = props;

  return (
    <SelectTableContext.Provider value={{ isOpen, onOpen, onClose }}>
      {children}
    </SelectTableContext.Provider>
  );
};

SelectTable.Popover = Popover;
SelectTable.Input = Input;
SelectTable.InputResults = InputResults;
SelectTable.Content = Content;

export { SelectTable };
